import { NgClass } from '@angular/common';
import { Component, effect, ElementRef, Input, input, OnDestroy, OnInit, output, viewChild, ViewEncapsulation } from '@angular/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Toast } from 'bootstrap';
import { fromEvent, take } from 'rxjs';

import { ContextType, ExternalUrl, ToastConfig } from '@bn/primitives';

@Component({
  selector: 'bn-web-ui-toast-component',
  templateUrl: './bn-toast.component.html',
  styleUrls: ['./bn-toast.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [NgClass, FaIconComponent],
})
export class BnToastComponent implements OnInit, OnDestroy {
  readonly toastEl = viewChild.required<ElementRef>('toastElement');

  @Input()
  public toastId!: string;

  @Input()
  public config!: ToastConfig;

  @Input()
  public type!: ContextType;

  @Input()
  public title?: string;

  @Input()
  public externalLink?: ExternalUrl;

  @Input()
  public headerIcon?: IconProp;

  @Input()
  public headerIconStyle?: ContextType;

  @Input()
  public customClass?: string;

  /**
   * Whether the autohide countdown may run right now. The toastr container clears it for toasts
   * covered by a newer one, and while the stack is hovered, so a toast is never dismissed before
   * it has been on top for its full delay. Defaults to true for standalone use, where there is no stack.
   */
  readonly canAutohide = input(true);

  public readonly disposeEvent = output();

  public toast!: Toast;

  private autohideTimer?: ReturnType<typeof setTimeout>;
  private isHovered = false;

  public constructor() {
    effect(() => (this.canAutohide() ? this.scheduleAutohide() : this.clearAutohide()));
  }

  ngOnInit(): void {
    this.show();
  }

  ngOnDestroy(): void {
    this.clearAutohide();
  }

  private show(): void {
    // Autohide is driven from canAutohide() below, so Bootstrap only handles the show/hide animation —
    // its own timer starts on show() and would expire while the toast sits behind a newer one
    this.toast = new Toast(this.toastEl().nativeElement, { ...this.config, autohide: false });

    fromEvent(this.toastEl().nativeElement, 'hidden.bs.toast')
      .pipe(take(1))
      .subscribe(() => this.hide());

    this.toast.show();
  }

  public hide(): void {
    this.clearAutohide();
    this.toast.dispose();
    this.disposeEvent.emit();
  }

  public onToastClick(): void {
    if (this.config.showClose) return this.hide();
  }

  /** Pointer or focus on the toast pauses the countdown, and leaving it starts a fresh one. */
  public onMouseEnter(): void {
    this.isHovered = true;
    this.clearAutohide();
  }

  public onMouseLeave(): void {
    this.isHovered = false;
    this.scheduleAutohide();
  }

  private scheduleAutohide(): void {
    this.clearAutohide();

    // canAutohide() is re-checked here because onMouseLeave() schedules outside the effect —
    // without it, hovering the peek of a covered toast would start its countdown early
    if (!this.canAutohide() || !this.config?.autohide || this.isHovered) return;

    this.autohideTimer = setTimeout(() => this.toast?.hide(), this.config.delay);
  }

  private clearAutohide(): void {
    clearTimeout(this.autohideTimer);
    this.autohideTimer = undefined;
  }
}
